import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import BackToTop from '../components/ui/BackToTop';

const EFFECTIVE_DATE = 'June 15, 2026';
const ORG_NAME = 'Second Story Initiative';
const CONTACT_EMAIL = 'eHunter@straydog-secondstory.org';
const DOMAIN = 'straydog-secondstory.org';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      <main id="main-content" className="py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-10">
            <Link
              to="/"
              className="inline-flex items-center text-sm text-primary-600 hover:text-primary-500 no-underline hover:no-underline mb-6"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Home
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
                <FileText className="h-5 w-5 text-primary-600" aria-hidden="true" />
              </div>
              <h1 className="text-3xl font-bold text-secondary-900">Terms of Service</h1>
            </div>
            <p className="text-sm text-secondary-500">
              Effective: {EFFECTIVE_DATE} · Last updated: {EFFECTIVE_DATE}
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-secondary max-w-none space-y-8">
            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">1. Acceptance of Terms</h2>
              <p className="text-secondary-700 leading-relaxed">
                By accessing or using the {ORG_NAME} platform at <strong>{DOMAIN}</strong>{' '}
                ("the Platform"), you agree to be bound by these Terms of Service. If you do not
                agree, please do not use the Platform.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">2. About the Platform</h2>
              <p className="text-secondary-700 leading-relaxed">
                {ORG_NAME} is a Rhode Island nonprofit corporation that provides technology education,
                AI-assisted mentoring, project-based learning, and mentorship matching services to
                justice-impacted individuals and the broader community. The Platform is provided
                free of charge to learners.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">3. User Accounts</h2>
              <ul className="list-disc pl-5 space-y-1 text-secondary-700">
                <li>You must provide accurate information when creating an account</li>
                <li>You are responsible for maintaining the security of your account credentials</li>
                <li>You must be at least 18 years of age to create an account</li>
                <li>One account per person; shared accounts are not permitted</li>
                <li>We reserve the right to suspend or terminate accounts that violate these terms</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">4. Acceptable Use</h2>
              <p className="text-secondary-700 leading-relaxed">You agree not to:</p>
              <ul className="list-disc pl-5 space-y-1 text-secondary-700 mt-2">
                <li>Use the Platform for any unlawful purpose</li>
                <li>Harass, abuse, or threaten other users, mentors, or staff</li>
                <li>Submit plagiarized work or misrepresent your qualifications</li>
                <li>Attempt to gain unauthorized access to platform systems or other user accounts</li>
                <li>Use automated tools (bots, scrapers) to access the Platform without permission</li>
                <li>Upload malicious code or content</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">5. Content &amp; Intellectual Property</h2>
              <h3 className="text-lg font-medium text-secondary-800 mt-4 mb-2">Your Content</h3>
              <p className="text-secondary-700 leading-relaxed">
                You retain ownership of code, projects, and other content you submit. By submitting
                content to the Platform, you grant {ORG_NAME} a non-exclusive, royalty-free license
                to use, display, and reproduce your content for Platform operations and
                promotional purposes (e.g., showcasing anonymized learner projects in grant
                applications).
              </p>
              <h3 className="text-lg font-medium text-secondary-800 mt-4 mb-2">Our Content</h3>
              <p className="text-secondary-700 leading-relaxed">
                Curriculum materials, learning modules, and platform features are the property of
                {ORG_NAME} and are provided for personal, non-commercial educational use. You may
                not redistribute or resell our educational content.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">6. AI-Assisted Services</h2>
              <p className="text-secondary-700 leading-relaxed">
                The Platform uses AI (Anthropic Claude) to provide mentoring, code reviews, and
                learning recommendations. AI-generated content is provided "as-is" for educational
                purposes and should not be relied upon as professional advice. {ORG_NAME} is not
                responsible for the accuracy or completeness of AI-generated responses.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">7. Donations</h2>
              <p className="text-secondary-700 leading-relaxed">
                {ORG_NAME} is a Rhode Island nonprofit corporation (EIN 42-2812566) with a
                federal 501(c)(3) application in preparation. Donations are <strong>not yet
                tax-deductible</strong> and will become deductible retroactively if and when
                the IRS grants recognition. No goods or services are provided in exchange for
                donations unless explicitly stated. Donations are non-refundable except in cases
                of processing error. For recurring donations, you may cancel at any time through
                the donation platform or by contacting
                us. Refund requests for processing errors should be directed to{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary-600 hover:text-primary-500">
                  {CONTACT_EMAIL}
                </a>{' '}
                within 30 days.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">8. Disclaimer of Warranties</h2>
              <p className="text-secondary-700 leading-relaxed">
                The Platform is provided "as is" and "as available" without warranties of any kind,
                either express or implied. We do not guarantee uninterrupted access, error-free
                operation, or specific learning outcomes. While we strive to connect learners with
                employment opportunities, we do not guarantee job placement.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">9. Limitation of Liability</h2>
              <p className="text-secondary-700 leading-relaxed">
                To the maximum extent permitted by law, {ORG_NAME} shall not be liable for any
                indirect, incidental, special, or consequential damages arising from your use of
                the Platform. Our total liability shall not exceed the amount you have donated to
                {ORG_NAME} in the 12 months preceding the claim, or $100, whichever is greater.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">10. Privacy</h2>
              <p className="text-secondary-700 leading-relaxed">
                Your use of the Platform is also governed by our{' '}
                <Link to="/privacy" className="text-primary-600 hover:text-primary-500">
                  Privacy Policy
                </Link>
                , which describes how we collect, use, and protect your information.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">11. Changes to Terms</h2>
              <p className="text-secondary-700 leading-relaxed">
                We may modify these Terms at any time. Material changes will be communicated via
                email or a prominent notice on the Platform. Continued use after changes constitutes
                acceptance of the updated Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">12. Governing Law</h2>
              <p className="text-secondary-700 leading-relaxed">
                These Terms are governed by the laws of the state in which {ORG_NAME} is
                incorporated, without regard to conflict of law provisions.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-secondary-900 mb-3">13. Contact</h2>
              <p className="text-secondary-700 leading-relaxed">
                For questions about these Terms, contact us at:
              </p>
              <p className="text-secondary-700 mt-2">
                <strong>{ORG_NAME}</strong><br />
                Email:{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary-600 hover:text-primary-500">
                  {CONTACT_EMAIL}
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>

      <PublicFooter />
      <BackToTop />
    </div>
  );
};

export default TermsOfService;
